import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, ClipboardList } from 'lucide-react';

/**
 * ProjectFormModal - Modal for creating or editing a project
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Function} onClose - Callback when modal is closed
 * @param {Function} onSubmit - Callback when form is submitted with project data
 * @param {Object} service - The service this project belongs to
 * @param {Object} projectToEdit - Optional existing project for edit mode
 */
const ProjectFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  service,
  projectToEdit = null 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'planning',
    start_date: new Date().toISOString().split('T')[0],
    target_completion_date: '',
    client_viewable: false,
    budget_hours: '',
    tags: [],
    service_id: service?.id || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fill form with existing project data when editing
  useEffect(() => {
    if (projectToEdit) {
      setFormData({
        name: projectToEdit.name || '',
        description: projectToEdit.description || '',
        status: projectToEdit.status || 'planning',
        start_date: projectToEdit.start_date ? new Date(projectToEdit.start_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        target_completion_date: projectToEdit.target_completion_date ? new Date(projectToEdit.target_completion_date).toISOString().split('T')[0] : '',
        client_viewable: projectToEdit.client_viewable || false,
        budget_hours: projectToEdit.budget_hours || '',
        tags: projectToEdit.tags || [],
        service_id: service?.id || ''
      });
    } else if (service) {
      // Reset form when adding new project
      setFormData({
        name: '',
        description: '',
        status: 'planning',
        start_date: new Date().toISOString().split('T')[0],
        target_completion_date: '',
        client_viewable: false,
        budget_hours: '',
        tags: [],
        service_id: service.id || ''
      });
    }
  }, [projectToEdit, service]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create a copy of form data to modify before submission
      const dataToSubmit = {
        ...formData,
        id: projectToEdit?.id, // Include ID if editing
      };
      
      // Handle empty budget_hours by sending null
      if (dataToSubmit.budget_hours === '' || dataToSubmit.budget_hours === undefined) {
        dataToSubmit.budget_hours = null;
      }
      
      // Handle empty target_completion_date by sending null instead of empty string
      if (dataToSubmit.target_completion_date === '' || dataToSubmit.target_completion_date === undefined) {
        dataToSubmit.target_completion_date = null;
      }
      
      await onSubmit(dataToSubmit);
      onClose();
    } catch (error) {
      console.error('Error submitting project:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-grayteck-900 bg-opacity-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="relative bg-white dark:bg-darkbg-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-grayteck-500 hover:text-grayteck-700 dark:text-grayteck-400 dark:hover:text-grayteck-200"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Modal header */}
          <div className="flex items-center mb-6">
            <div className="mr-3 flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-grayteck-100 dark:bg-darkbg-700">
              <ClipboardList className="h-6 w-6 text-grayteck-600 dark:text-grayteck-400" />
            </div>
            <h3 className="text-lg font-medium text-grayteck-900 dark:text-grayteck-100">
              {projectToEdit ? 'Edit Project' : 'Create New Project'}
            </h3>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Project Name */}
            <div>
              <label 
                htmlFor="name" 
                className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
              >
                Project Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                         rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                         focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                placeholder="Enter project name"
              />
            </div>

            {/* Project Description */}
            <div>
              <label 
                htmlFor="description" 
                className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                         rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                         focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                placeholder="Describe the project"
              />
            </div>

            {/* Project Status */}
            <div>
              <label 
                htmlFor="status" 
                className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
              >
                Status *
              </label>
              <select
                id="status"
                name="status"
                required
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                         rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                         focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
              >
                <option value="planning">Planning</option>
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Dates Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Start Date */}
              <div>
                <label 
                  htmlFor="start_date" 
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Start Date *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-grayteck-400" />
                  </div>
                  <input
                    id="start_date"
                    name="start_date"
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={handleChange}
                    className="w-full pl-10 px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                             rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                             focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                  />
                </div>
              </div>

              {/* Target Completion Date */}
              <div>
                <label 
                  htmlFor="target_completion_date" 
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Target Completion Date
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-grayteck-400" />
                  </div>
                  <input
                    id="target_completion_date"
                    name="target_completion_date"
                    type="date"
                    value={formData.target_completion_date}
                    onChange={handleChange}
                    className="w-full pl-10 px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                             rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                             focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                  />
                </div>
              </div>
            </div>

            {/* Budget Hours and Client Viewable Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Budget Hours */}
              <div>
                <label 
                  htmlFor="budget_hours" 
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Budget Hours
                </label>
                <input
                  id="budget_hours"
                  name="budget_hours"
                  type="number"
                  min="0"
                  step="0.25"
                  value={formData.budget_hours}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                           rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                           focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                  placeholder="Enter budget hours"
                />
              </div>

              {/* Client Viewable */}
              <div className="flex items-center mt-6">
                <input
                  id="client_viewable"
                  name="client_viewable"
                  type="checkbox"
                  checked={formData.client_viewable}
                  onChange={(e) => setFormData({...formData, client_viewable: e.target.checked})}
                  className="h-4 w-4 text-grayteck-600 focus:ring-grayteck-500 border-grayteck-300 
                           rounded dark:bg-darkbg-700 dark:border-grayteck-600"
                />
                <label 
                  htmlFor="client_viewable" 
                  className="ml-2 block text-sm font-medium text-grayteck-700 dark:text-grayteck-300"
                >
                  Visible to client
                </label>
              </div>
            </div>
            
            {/* Tags */}
            <div>
              <label 
                htmlFor="tags" 
                className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
              >
                Tags <span className="text-xs text-grayteck-500">(comma separated)</span>
              </label>
              <input
                id="tags"
                name="tags"
                type="text"
                value={Array.isArray(formData.tags) ? formData.tags.join(', ') : formData.tags}
                onChange={(e) => {
                  const tagsArray = e.target.value
                    .split(',')
                    .map(tag => tag.trim())
                    .filter(tag => tag !== '');
                  setFormData({...formData, tags: tagsArray});
                }}
                className="w-full px-3 py-2 border border-grayteck-300 dark:border-grayteck-700 
                         rounded-md shadow-sm focus:outline-none focus:ring-grayteck-500 
                         focus:border-grayteck-500 dark:bg-darkbg-700 dark:text-grayteck-100"
                placeholder="E.g. design, urgent, phase1"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-grayteck-200 dark:border-grayteck-700">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-700 dark:text-grayteck-300 
                       rounded-md hover:bg-grayteck-200 dark:hover:bg-darkbg-600 focus:outline-none"
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isSubmitting}
                className={`px-4 py-2 bg-grayteck-600 text-white rounded-md hover:bg-grayteck-700 focus:outline-none
                        flex items-center justify-center min-w-[100px] ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  projectToEdit ? 'Update Project' : 'Create Project'
                )}
              </motion.button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ProjectFormModal;